import { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  addToast,
  Spinner,
} from "@heroui/react";
import {
  Download,
  ExternalLink,
  RotateCw,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useMediaQuery } from "@mantine/hooks";
import { FilePreview } from "./FilePreview";
import { canPreviewFile, getFileType } from "@/utils/file-utils";
import { AlertButton } from "./AlertButton";
import { api } from "@/lib/api-client";

interface FilePreviewModalProps {
  fileName: string;
  fileLabel?: string;
  fileId: number;
  getPresignedUrl: (
    fileId: number,
    asAttachment?: boolean,
  ) => Promise<{ presignedUrl: string; fileName?: string }>;
  isLoading?: boolean;
  children: (props: { onClick: () => void }) => React.ReactNode; // Render prop pattern
  /** When set, a customer-file delete action is shown. Omit for read-only
   * previews (e.g. invoices) where deletion does not apply. */
  customerId?: number;
}

export const FilePreviewModal = ({
  fileName,
  fileLabel,
  fileId,
  getPresignedUrl,
  isLoading = false,
  children,
  customerId,
}: FilePreviewModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [rotation, setRotation] = useState(0);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isImage = getFileType(fileName) === "image";
  const canPreview = canPreviewFile(fileName);

  // Resolved synchronously so the action bar doesn't render labelled buttons
  // for a frame before collapsing to icons.
  const isMobile =
    useMediaQuery("(max-width: 768px)", false, {
      getInitialValueInEffect: false,
    }) ?? false;

  const { mutateAsync: deleteCustomerFile, isPending: isDeletingCustomerFile } =
    api.storage.useDeleteCustomerFile();

  const handleRotate = () => {
    setRotation((prevRotation) => (prevRotation + 90) % 360);
  };

  const handleOpenPreview = async () => {
    if (!canPreview) {
      // If can't preview, just open in new tab
      try {
        const presignedUrlData = await getPresignedUrl(fileId);
        window.open(
          presignedUrlData.presignedUrl,
          "_blank",
          "noopener,noreferrer",
        );
      } catch (error) {
        console.error("Error getting presigned URL:", error);
        addToast({
          title: t`Napaka`,
          description: t`Prišlo je do napake pri odpiranju datoteke. Poskusite znova.`,
          color: "danger",
        });
      }
      return;
    }

    // For previewable files, open modal
    setRotation(0);
    setIsLoadingPreview(true);
    setPreviewError(null);

    try {
      const presignedUrlData = await getPresignedUrl(fileId);
      setPreviewUrl(presignedUrlData.presignedUrl);
      onOpen();
    } catch (error) {
      console.error("Error getting presigned URL:", error);
      setPreviewError(t`Napaka pri pridobivanju predogleda`);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleDownload = async () => {
    try {
      const presignedUrlData = await getPresignedUrl(fileId, true);

      // Create an ephemeral <a> to force download
      const link = document.createElement("a");
      link.href = presignedUrlData.presignedUrl;
      link.download = presignedUrlData.fileName ?? fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading file:", error);
      addToast({
        title: t`Napaka`,
        description: t`Prišlo je do napake pri prenosu datoteke. Poskusite znova.`,
        color: "danger",
      });
    }
  };

  const handleOpenInNewTab = async () => {
    try {
      const presignedUrlData = await getPresignedUrl(fileId);
      window.open(
        presignedUrlData.presignedUrl,
        "_blank",
        "noopener,noreferrer",
      );
    } catch (error) {
      console.error("Error getting presigned URL:", error);
      addToast({
        title: t`Napaka`,
        description: t`Prišlo je do napake pri odpiranju datoteke v novem zavihku. Poskusite znova.`,
        color: "danger",
      });
    }
  };

  const handleModalClose = () => {
    onClose();
    // Clear preview URL after a short delay to prevent flash
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setPreviewUrl(null);
      setPreviewError(null);
      setRotation(0);
    }, 300); // Increased delay for smoother transition
  };

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, []);

  if (isImage) {
    const isSideways = rotation === 90 || rotation === 270;
    return (
      <>
        {children({ onClick: handleOpenPreview })}
        <Modal
          isOpen={isOpen}
          onClose={handleModalClose}
          size="full"
          classNames={{
            base: "bg-black/80 backdrop-blur-sm",
            wrapper: "!h-screen !w-screen",
            // content: "bg-transparent shadow-none !h-screen !w-screen",
            body: "p-0",
            closeButton:
              "z-20 text-white hover:bg-white/20 top-[calc(var(--safe-area-top,0px)+1rem)] right-[calc(var(--safe-area-right,0px)+1rem)]",
          }}
          placement="center"
          isDismissable={true}
          isKeyboardDismissDisabled={false}
        >
          <ModalContent>
            <ModalBody
              className="relative flex h-full w-full items-center justify-center"
              // Allow closing the modal by clicking on the backdrop (the body)
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  handleModalClose();
                }
              }}
            >
              {isLoadingPreview ? (
                <div className="flex flex-col items-center justify-center gap-4">
                  <Spinner size="lg" color="white" />
                  <p className="text-white/80">
                    <Trans>Nalaganje predogleda...</Trans>
                  </p>
                </div>
              ) : previewError ? (
                <div className="bg-danger/20 flex flex-col items-center justify-center gap-2 rounded-lg p-8">
                  <TriangleAlert className="text-danger h-8 w-8" />
                  <p className="text-danger text-center">{previewError}</p>
                </div>
              ) : previewUrl ? (
                <>
                  <div className="absolute top-[calc(var(--safe-area-top,0px)+1.5rem)] left-1/2 z-20 -translate-x-1/2 transform">
                    <div className="flex max-w-[90vw] items-center gap-2 rounded-full bg-black/50 px-3 py-2 shadow-lg">
                      <span
                        className="max-w-[30vw] truncate text-xs text-white/90 md:max-w-[40vw] md:text-sm"
                        title={fileLabel ?? fileName}
                      >
                        {fileLabel ? `${fileLabel} (${fileName})` : fileName}
                      </span>
                    </div>
                  </div>
                  <img
                    src={previewUrl}
                    alt={fileName}
                    className={`rounded-2xl object-contain transition-transform duration-200 ${
                      isSideways
                        ? "max-h-[90vw] max-w-[90vh]"
                        : "max-h-[90vh] max-w-[90vw]"
                    }`}
                    style={{ transform: `rotate(${rotation}deg)` }}
                  />
                  <div className="absolute bottom-[calc(var(--safe-area-bottom,0px)+1.5rem)] left-1/2 z-20 -translate-x-1/2 transform">
                    <div className="flex max-w-[90vw] items-center gap-2 rounded-full bg-black/50 px-3 py-2 shadow-lg">
                      <Button
                        isIconOnly={isMobile}
                        size="sm"
                        variant="flat"
                        className="rounded-l-2xl text-white/90"
                        startContent={
                          isMobile ? undefined : <Download size={16} />
                        }
                        onPress={handleDownload}
                        isLoading={isLoading}
                        aria-label={t`Prenesi`}
                      >
                        {isMobile ? (
                          <Download size={16} />
                        ) : (
                          <Trans>Prenesi</Trans>
                        )}
                      </Button>
                      <Button
                        isIconOnly
                        size="sm"
                        variant="flat"
                        className="text-white/90"
                        onPress={handleRotate}
                        aria-label={t`Rotate image`}
                      >
                        <RotateCw size={16} />
                      </Button>
                      <Button
                        isIconOnly={isMobile}
                        size="sm"
                        variant="flat"
                        className="text-white/90"
                        startContent={
                          isMobile ? undefined : <ExternalLink size={16} />
                        }
                        onPress={handleOpenInNewTab}
                        isLoading={isLoading}
                        aria-label={t`Odpri v novem zavihku`}
                      >
                        {isMobile ? (
                          <ExternalLink size={16} />
                        ) : (
                          <Trans>Odpri v novem zavihku</Trans>
                        )}
                      </Button>
                      {customerId != null && (
                        <AlertButton
                          title={t`Izbriši datoteko`}
                          content={t`Ali ste prepričani, da želite izbrisati datoteko?`}
                          tooltip={t`Izbriši datoteko`}
                          buttonProps={{
                            isIconOnly: isMobile,
                            variant: "flat",
                            startContent: isMobile ? undefined : (
                              <Trash2 size={16} />
                            ),
                            color: "danger",
                            size: "sm",
                            "aria-label": t`Izbriši datoteko`,
                            className: "rounded-r-2xl",
                          }}
                          confirmProps={{
                            label: t`Izbriši datoteko`,
                            onClick: async () => {
                              try {
                                await deleteCustomerFile({
                                  id: customerId,
                                  fileId,
                                });
                                handleModalClose();
                              } catch (error) {
                                console.error("Error deleting file:", error);
                                addToast({
                                  title: t`Napaka`,
                                  description: t`Prišlo je do napake pri izbrisu datoteke. Poskusite znova.`,
                                  color: "danger",
                                });
                              }
                            },
                            isLoading: isDeletingCustomerFile,
                          }}
                        >
                          {isMobile ? (
                            <Trash2 size={16} />
                          ) : (
                            <Trans>Izbriši datoteko</Trans>
                          )}
                        </AlertButton>
                      )}
                    </div>
                  </div>
                </>
              ) : null}
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    );
  }

  // Fallback for PDFs and other file types
  return (
    <>
      {children({ onClick: handleOpenPreview })}
      <Modal
        isOpen={isOpen}
        onClose={handleModalClose}
        size="5xl"
        classNames={{
          body: "p-0",
          base: "max-h-[90vh] md:max-h-[90vh]",
          wrapper: "md:p-4 p-2",
        }}
        isDismissable={true}
        isKeyboardDismissDisabled={false}
        placement="center"
      >
        <ModalContent>
          {/* pr-* reserves space for HeroUI's absolutely-positioned close
              ("x") button so it does not overlap the right-most header action
              (e.g. "Odpri v novem zavihku" on invoice previews). */}
          <ModalHeader className="flex items-center justify-between gap-4 py-3 pr-12 pl-4 md:py-4 md:pr-14 md:pl-6">
            <h3 className="truncate text-base font-semibold md:text-lg">
              {fileName}
            </h3>
            <div className="flex flex-shrink-0 items-center gap-2">
              <Button
                size="sm"
                variant="flat"
                startContent={<Download size={16} />}
                onPress={handleDownload}
                isLoading={isLoading}
              >
                <Trans>Prenesi</Trans>
              </Button>
              <Button
                size="sm"
                variant="flat"
                startContent={<ExternalLink size={16} />}
                onPress={handleOpenInNewTab}
                isLoading={isLoading}
              >
                <Trans>Odpri v novem zavihku</Trans>
              </Button>
              {customerId != null && (
                <AlertButton
                  title={t`Izbriši datoteko`}
                  content={t`Ali ste prepričani, da želite izbrisati datoteko?`}
                  tooltip={t`Izbriši datoteko`}
                  buttonProps={{
                    variant: "flat",
                    startContent: <Trash2 size={16} />,
                    color: "danger",
                    size: "sm",
                    "aria-label": t`Izbriši datoteko`,
                  }}
                  confirmProps={{
                    label: t`Izbriši datoteko`,
                    onClick: async () => {
                      try {
                        await deleteCustomerFile({
                          id: customerId,
                          fileId,
                        });
                        handleModalClose();
                      } catch (error) {
                        console.error("Error deleting file:", error);
                        addToast({
                          title: t`Napaka`,
                          description: t`Prišlo je do napake pri izbrisu datoteke. Poskusite znova.`,
                          color: "danger",
                        });
                      }
                    },
                    isLoading: isDeletingCustomerFile,
                  }}
                >
                  <Trans>Izbriši datoteko</Trans>
                </AlertButton>
              )}
            </div>
          </ModalHeader>

          <ModalBody className="px-4 md:px-6">
            {isLoadingPreview ? (
              <div className="flex h-[70vh] flex-col items-center justify-center gap-4">
                <Spinner size="lg" />
                <p className="text-gray-500">
                  <Trans>Nalaganje predogleda...</Trans>
                </p>
              </div>
            ) : previewError ? (
              <div className="flex h-[70vh] flex-col items-center justify-center gap-2">
                <TriangleAlert className="text-danger h-8 w-8" />
                <p className="text-danger text-center">{previewError}</p>
              </div>
            ) : previewUrl ? (
              <div className="h-[70vh]">
                <FilePreview
                  fileUrl={previewUrl}
                  fileName={fileName}
                  className="h-full w-full"
                />
              </div>
            ) : null}
          </ModalBody>

          <ModalFooter className="px-4 py-3 md:px-6 md:py-4">
            <Button color="primary" variant="flat" onPress={handleModalClose}>
              <Trans>Zapri</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
